/**
 * User Preferences Atoms
 *
 * Jotai atoms for managing user preferences including language settings
 * Integrates with localStorage and Firestore for persistence
 */

import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { SupportedLanguage, DEFAULT_LANGUAGE } from '@/i18n';

// User preferences interface
export interface UserPreferences {
  language: SupportedLanguage;
}

// Default user preferences
const defaultPreferences: UserPreferences = {
  language: DEFAULT_LANGUAGE,
};

// Language preference atom with localStorage persistence.
// Uses its own storage key ('languagePreference'), distinct from i18next's own
// 'i18nextLng' key (see src/i18n/index.ts) - i18next stores a plain string there,
// while atomWithStorage JSON-encodes its value, so sharing the key caused the two
// writers to clobber each other's format and silently reset the language to the
// default on the next load.
export const languagePreferenceAtom = atomWithStorage<SupportedLanguage>(
  'languagePreference',
  DEFAULT_LANGUAGE
);

// Complete user preferences atom with localStorage persistence
export const userPreferencesAtom = atomWithStorage<UserPreferences>(
  'userPreferences',
  defaultPreferences
);

// Language atom that syncs with i18n
export const currentLanguageAtom = atom(
  get => get(languagePreferenceAtom),
  (get, set, newLanguage: SupportedLanguage) => {
    // Update the language preference
    set(languagePreferenceAtom, newLanguage);

    const current = get(userPreferencesAtom);
    set(userPreferencesAtom, { ...current, language: newLanguage });
  }
);

// Reset preferences atom
export const resetUserPreferencesAtom = atom(null, (_get, set) => {
  set(userPreferencesAtom, defaultPreferences);
  set(languagePreferenceAtom, DEFAULT_LANGUAGE);
});

// Update specific preference atom
export const updateUserPreferenceAtom = atom(
  null,
  (get, set, updates: Partial<UserPreferences>) => {
    const current = get(userPreferencesAtom);
    set(userPreferencesAtom, { ...current, ...updates });
  }
);

// Language change effect atom (for side effects)
export const languageChangeEffectAtom = atom(null, (_get, set, newLanguage: SupportedLanguage) => {
  // Update the language atom
  set(currentLanguageAtom, newLanguage);
  return newLanguage;
});
